import React, { useEffect } from 'react';
import { DroneListProps } from './types';
import { ArcPagination, ArcSkeleton, ArcStack } from '@src/core/ui';
import { ArcDrone } from '@src/core/components';
import { useDispatch } from 'react-redux';
import { setSelectedDrone } from '@src/features/fleet/pages/drones-page/redux';
import { DroneEntity } from '@src/core/features';
import { useFleetDroneUrlSync, usePaginatedDrones } from '@src/features/fleet/pages/drones-page/hooks';
import { PAGINATION_DEFAULT_SIZE } from '@src/core/config';

const filterDronesBySearch = (drones: DroneEntity[], q: string): DroneEntity[] => {
  if (!q) return drones;
  const lower = q.trim().toLowerCase();
  return drones.filter((drone) => {
    const nameMatch = drone.name?.toLowerCase().includes(lower) ?? false;
    const serialMatch = drone.serialNumber?.toLowerCase().includes(lower) ?? false;
    return nameMatch || serialMatch;
  });
};

const DroneList: React.FC<DroneListProps> = ({ searchName = '', hubId = null }) => {
  const dispatch = useDispatch();
  const [clientPage, setClientPage] = React.useState(0);
  const { drones, allDrones, isLoading, pagination } = usePaginatedDrones(hubId);

  const isSearchMode = Boolean(searchName.trim());
  const isClientFilterMode = isSearchMode;

  const filteredDrones = React.useMemo(() => {
    if (!isSearchMode) return drones;
    return filterDronesBySearch(allDrones, searchName);
  }, [isSearchMode, allDrones, drones, searchName]);

  const totalFilteredCount = filteredDrones.length;
  const clientTotalPages = Math.ceil(totalFilteredCount / PAGINATION_DEFAULT_SIZE) || 0;
  const paginatedFilteredDrones = React.useMemo(() => {
    if (!isClientFilterMode) return filteredDrones;
    const start = clientPage * PAGINATION_DEFAULT_SIZE;
    return filteredDrones.slice(start, start + PAGINATION_DEFAULT_SIZE);
  }, [isClientFilterMode, filteredDrones, clientPage]);

  const displayDrones = isClientFilterMode ? paginatedFilteredDrones : filteredDrones;
  const totalCount = isClientFilterMode ? totalFilteredCount : pagination.totalCount;
  const totalPages = isClientFilterMode
    ? clientTotalPages
    : Math.ceil(pagination.totalCount / PAGINATION_DEFAULT_SIZE) || 0;
  const currentPage = isClientFilterMode ? clientPage : pagination.page;
  const showPagination = totalCount > 0 && totalPages > 1;

  const { selectedDrone } = useFleetDroneUrlSync({
    allDrones,
    displayDrones,
    isLoading,
    page: pagination.page,
    onPageChange: pagination.onPageChange,
  });

  useEffect(() => {
    setClientPage(0);
  }, [searchName]);

  const onToggleDrone = (drone: DroneEntity) => {
    if (selectedDrone?.id === drone.id) {
      dispatch(setSelectedDrone(null));
      return;
    }
    dispatch(setSelectedDrone(drone));
  };

  const handlePageChange = React.useCallback(
    (_: unknown, page: number) => {
      if (isClientFilterMode) {
        setClientPage(page - 1);
      } else {
        pagination.onPageChange(page - 1);
      }
    },
    [isClientFilterMode, pagination],
  );

  if (isLoading) {
    return (
      <ArcStack spacing={1}>
        <ArcSkeleton height={80} />
        <ArcSkeleton height={80} />
        <ArcSkeleton height={80} />
        <ArcSkeleton height={80} />
      </ArcStack>
    );
  }

  return (
    <ArcStack sx={{ flex: 1, minHeight: 0 }}>
      <ArcStack
        spacing={1}
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
        }}>
        {displayDrones.map((drone) => (
          <ArcDrone.primary
            key={drone.id}
            drone={drone}
            selected={selectedDrone?.id === drone.id}
            onClick={() => onToggleDrone(drone)}
          />
        ))}
      </ArcStack>

      {showPagination && (
        <ArcStack alignItems='center' mt={2}>
          <ArcPagination page={currentPage + 1} count={totalPages} onChange={handlePageChange} />
        </ArcStack>
      )}
    </ArcStack>
  );
};

export default DroneList;
